import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ApiService from '../../../common/ApiCalls'
import { Loader } from '../../../components/1elements'
import FullView from '../../../components/5templates/FullView'
import {
  formatDashboardFeedbackDate,
  getDashboardFeedbackCategoryLabel
} from '../../../features/dashboard-v1/dashboardFeedbackModel'

const FEEDBACK_REQUESTS = Object.freeze({
  nf: { action: 'Add', heading: 'New feedback approval' },
  ef: { action: 'Edit', heading: 'Feedback change approval' },
  df: { action: 'Delete', heading: 'Feedback removal approval' }
})

const REJECTION_REASON_MAX_LENGTH = 500

const valueOrFallback = (value, fallback = 'Not provided') =>
  value === null || value === undefined || value === '' ? fallback : value

const snapshot = (content = {}) => ({
  category: content.category ?? null,
  comment: content.comment ?? '',
  contactEmail: content.contactEmail ?? null,
  contactName: content.contactName ?? content.commenterName ?? null,
  contactPhone: content.contactPhone ?? null,
  ownerUserId: content.ownerUserId ?? null,
  receivedDate: content.receivedDate ?? content.date ?? null,
  title: content.title ?? null
})

const errorMessage = (error, fallback) => {
  const responseMessage = error?.response?.data
  if (typeof responseMessage === 'string') return responseMessage
  return (
    responseMessage?.errors?.[0]?.message ||
    error?.message ||
    fallback
  )
}

const Detail = ({ label, value }) => (
  <Box>
    <Typography color="text.secondary" component="dt" variant="caption">
      {label}
    </Typography>
    <Typography component="dd" sx={{ m: 0, overflowWrap: 'anywhere' }}>
      {valueOrFallback(value)}
    </Typography>
  </Box>
)

const FeedbackSnapshot = ({ caption, feedback }) => (
  <Paper
    elevation={0}
    sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}
  >
    <Typography component="h2" fontWeight={700} variant="subtitle1">
      {caption}
    </Typography>
    <Stack component="dl" spacing={1.5} sx={{ m: 0, mt: 2 }}>
      <Detail label="Title" value={feedback.title} />
      <Detail
        label="Category"
        value={getDashboardFeedbackCategoryLabel(feedback.category)}
      />
      <Detail
        label="Received date"
        value={formatDashboardFeedbackDate(feedback.receivedDate)}
      />
      <Detail label="Feedback" value={feedback.comment} />
      <Divider />
      <Detail label="Contact name" value={feedback.contactName} />
      <Detail label="Contact email" value={feedback.contactEmail} />
      <Detail label="Contact phone" value={feedback.contactPhone} />
      <Detail
        label="Owner"
        value={feedback.ownerUserId ? 'Assigned' : 'Unassigned'}
      />
    </Stack>
  </Paper>
)

const ApproveFeedback = () => {
  const navigate = useNavigate()
  const { pendingDataId } = useParams()
  const [record, setRecord] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [decision, setDecision] = useState(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const [rejectionError, setRejectionError] = useState(null)
  const [decisionError, setDecisionError] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [successMessage, setSuccessMessage] = useState(null)

  useEffect(() => {
    let active = true

    const load = async () => {
      setIsLoading(true)
      setLoadError(null)
      try {
        const pending = await ApiService().getPendingDashboardData(pendingDataId)
        if (!pending || !FEEDBACK_REQUESTS[pending.import_type]) {
          throw new Error('This feedback approval request is no longer available.')
        }
        if (active) setRecord(pending)
      } catch (error) {
        if (active) {
          setLoadError(
            errorMessage(error, 'The feedback approval request could not be loaded.')
          )
        }
      } finally {
        if (active) setIsLoading(false)
      }
    }

    load()
    return () => {
      active = false
    }
  }, [pendingDataId])

  const request = record ? FEEDBACK_REQUESTS[record.import_type] : null
  const payload = useMemo(() => record?.comment_data ?? {}, [record])
  const currentFeedback = useMemo(
    () =>
      snapshot(
        record?.import_type === 'ef' ? payload.baseContent : payload
      ),
    [payload, record?.import_type]
  )
  const proposedFeedback = useMemo(
    () =>
      record?.import_type === 'ef'
        ? snapshot(payload.replacementContent)
        : currentFeedback,
    [currentFeedback, payload, record?.import_type]
  )
  const isPending =
    record?.status === 'Pending Approval' || record?.status === 'pending'
  const assignedApproverId = record?.approver_id ?? record?.approverId
  const isAssignedApprover =
    Boolean(assignedApproverId) &&
    assignedApproverId === localStorage.getItem('userId')
  const canDecide = isPending && isAssignedApprover && !successMessage

  const closeDecision = () => {
    if (isSaving) return
    setDecision(null)
    setDecisionError(null)
    setRejectionError(null)
  }

  const approve = async () => {
    setIsSaving(true)
    setDecisionError(null)
    try {
      await ApiService().approvePendingDashboardData(pendingDataId)
      setSuccessMessage('The feedback request has been approved.')
      setDecision(null)
    } catch (error) {
      setDecisionError(
        errorMessage(error, 'The feedback request could not be approved.')
      )
    } finally {
      setIsSaving(false)
    }
  }

  const reject = async () => {
    const reason = rejectionReason.trim()
    if (!reason) {
      setRejectionError('A reason for rejection is required.')
      return
    }

    setIsSaving(true)
    setDecisionError(null)
    try {
      await ApiService().rejectPendingDashboardData(
        pendingDataId,
        reason,
        record.initiator_email
      )
      setSuccessMessage('The feedback request has been rejected.')
      setDecision(null)
    } catch (error) {
      setDecisionError(
        errorMessage(error, 'The feedback request could not be rejected.')
      )
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) return <Loader />

  return (
    <FullView disabled noWidgets>
      <Box sx={{ height: '100%', overflowY: 'auto', p: { xs: 2, md: 3 } }}>
        <Box sx={{ maxWidth: 1050, mx: 'auto' }}>
          <Button onClick={() => navigate(-1)} sx={{ mb: 2 }}>
            Back to approvals
          </Button>

          {loadError ? (
            <Alert severity="error">
              <AlertTitle>Feedback approval could not be loaded</AlertTitle>
              {loadError}
            </Alert>
          ) : (
            <>
              <Stack
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={1}
              >
                <Box>
                  <Typography component="h1" fontSize={28} fontWeight={700}>
                    {request.heading}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                    Review the feedback details before making a decision.
                  </Typography>
                </Box>
                <Chip
                  color={isPending ? 'warning' : 'default'}
                  label={valueOrFallback(record.status, 'Unknown status')}
                  size="small"
                />
              </Stack>

              {successMessage && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {successMessage}
                </Alert>
              )}

              {!isAssignedApprover && isPending && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  You can view this request, but only its assigned approver can
                  approve or reject it.
                </Alert>
              )}

              {record.import_type === 'ef' ? (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                    mt: 3
                  }}
                >
                  <FeedbackSnapshot
                    caption="Current feedback"
                    feedback={currentFeedback}
                  />
                  <FeedbackSnapshot
                    caption="Proposed feedback"
                    feedback={proposedFeedback}
                  />
                </Box>
              ) : (
                <Box sx={{ mt: 3 }}>
                  <FeedbackSnapshot
                    caption={
                      record.import_type === 'df'
                        ? 'Feedback to remove'
                        : 'Proposed feedback'
                    }
                    feedback={proposedFeedback}
                  />
                </Box>
              )}

              {record.import_type === 'df' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <AlertTitle>Removal reason</AlertTitle>
                  {valueOrFallback(payload.deletionReason)}
                </Alert>
              )}

              <Paper
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'divider', mt: 2, p: 2 }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    Requested by{' '}
                    {valueOrFallback(
                      record.initiator_name || record.initiator_email,
                      'Unknown user'
                    )}
                  </Typography>
                  <Typography color="text.secondary" variant="caption">
                    Request type: {request.action} feedback
                  </Typography>
                  <Typography color="text.secondary" variant="caption">
                    Location ID: {valueOrFallback(payload.locationId)}
                  </Typography>
                </Stack>
                {canDecide && (
                  <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
                    <Button
                      color="error"
                      onClick={() => {
                        setDecision('reject')
                        setRejectionReason('')
                        setDecisionError(null)
                      }}
                      variant="outlined"
                    >
                      Reject feedback
                    </Button>
                    <Button
                      color="success"
                      onClick={() => {
                        setDecision('approve')
                        setDecisionError(null)
                      }}
                      variant="contained"
                    >
                      Approve feedback
                    </Button>
                  </Stack>
                )}
              </Paper>
            </>
          )}
        </Box>

        <Dialog fullWidth maxWidth="sm" onClose={closeDecision} open={decision === 'approve'}>
          <DialogTitle>Approve feedback request?</DialogTitle>
          <DialogContent>
            <Typography>
              This will apply the requested {request?.action.toLowerCase()} action.
            </Typography>
            {decisionError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {decisionError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSaving} onClick={closeDecision}>
              Cancel
            </Button>
            <Button
              color="success"
              disabled={isSaving}
              onClick={approve}
              variant="contained"
            >
              {isSaving ? 'Approving…' : 'Confirm approval'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog fullWidth maxWidth="sm" onClose={closeDecision} open={decision === 'reject'}>
          <DialogTitle>Reject feedback request?</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              error={Boolean(rejectionError)}
              fullWidth
              helperText={
                rejectionError ||
                `${rejectionReason.length}/${REJECTION_REASON_MAX_LENGTH}`
              }
              inputProps={{ maxLength: REJECTION_REASON_MAX_LENGTH }}
              label="Reason for rejection"
              minRows={3}
              multiline
              onChange={(event) => {
                setRejectionReason(event.target.value)
                setRejectionError(null)
              }}
              required
              sx={{ mt: 1 }}
              value={rejectionReason}
            />
            {decisionError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {decisionError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSaving} onClick={closeDecision}>
              Cancel
            </Button>
            <Button
              color="error"
              disabled={isSaving}
              onClick={reject}
              variant="contained"
            >
              {isSaving ? 'Rejecting…' : 'Confirm rejection'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </FullView>
  )
}

export default ApproveFeedback
